import { getBrowserPostHog } from "@/lib/posthog-browser";

type DomGuardMethod = "removeChild" | "insertBefore";

let installed = false;
let originalRemoveChild: typeof Node.prototype.removeChild | null = null;
let originalInsertBefore: typeof Node.prototype.insertBefore | null = null;

function isNotFoundDomError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "NotFoundError";
}

function recordGuardTrigger(method: DomGuardMethod): void {
  getBrowserPostHog()?.capture("dom_reconciliation_guard_triggered", {
    method,
    exception_name: "NotFoundError",
  });
}

/**
 * Patches Node.prototype.removeChild and insertBefore so React survives DOM
 * mutations from browser translation (e.g. Google Translate wrapping text nodes).
 * Still records a PostHog event when the guard activates.
 */
export function installDomReconciliationGuard(): void {
  if (installed || typeof window === "undefined") {
    return;
  }

  installed = true;

  originalRemoveChild = Node.prototype.removeChild;
  originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    try {
      return originalRemoveChild!.call(this, child) as T;
    } catch (error) {
      if (isNotFoundDomError(error) && child.parentNode !== this) {
        recordGuardTrigger("removeChild");
        return child;
      }
      throw error;
    }
  };

  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    try {
      return originalInsertBefore!.call(this, newNode, referenceNode) as T;
    } catch (error) {
      if (
        isNotFoundDomError(error) &&
        referenceNode !== null &&
        referenceNode.parentNode !== this
      ) {
        recordGuardTrigger("insertBefore");
        return newNode;
      }
      throw error;
    }
  };
}

/** @internal Test-only reset for guard install state and prototype patches. */
export function resetDomReconciliationGuardForTests(): void {
  if (originalRemoveChild) {
    Node.prototype.removeChild = originalRemoveChild;
  }
  if (originalInsertBefore) {
    Node.prototype.insertBefore = originalInsertBefore;
  }
  installed = false;
  originalRemoveChild = null;
  originalInsertBefore = null;
}
